import * as Y from 'yjs';
import { api, base64ToBytes, bytesToBase64 } from './api';
import { duplicateBoardRemote, type BoardSummary } from './boardsApi';

/**
 * Duplicates a board. The server copies the Yjs log and the files, but it never decodes Yjs,
 * so the copied document still points at the source board's asset ids. Here we load the copy,
 * rewrite every image/file/cover reference to the new ids and push that as the copy's first edit.
 */
export async function duplicateBoard(sourceId: string, title: string): Promise<BoardSummary> {
  const { board, assetMap } = await duplicateBoardRemote(sourceId, title);
  if (Object.keys(assetMap).length) {
    try {
      await remapAssets(board.id, assetMap);
    } catch (e) {
      // the board exists; its images would show as missing until this is retried
      console.warn('[duplicate] asset references not rewritten', e);
      throw new Error('Board copied, but its image references could not be updated. Open it and check.');
    }
  }
  return board;
}

interface DocResponse {
  snapshot: string | null;
  updates: { data: string }[];
}

async function remapAssets(boardId: string, map: Record<string, string>): Promise<void> {
  const res = await api.get<DocResponse>(`/boards/${boardId}/doc`);
  const doc = new Y.Doc();
  try {
    if (res.snapshot) Y.applyUpdate(doc, base64ToBytes(res.snapshot));
    for (const u of res.updates) Y.applyUpdate(doc, base64ToBytes(u.data));
    const items = doc.getMap<Y.Map<unknown>>('items');
    const updates: Uint8Array[] = [];
    const capture = (u: Uint8Array) => updates.push(u);
    doc.on('update', capture);
    doc.transact(() => {
      items.forEach((y) => {
        const props = y.get('props');
        if (!(props instanceof Y.Map)) return;
        for (const key of ['assetId', 'coverAssetId']) {
          const assetId = props.get(key);
          if (typeof assetId === 'string' && map[assetId]) props.set(key, map[assetId]);
        }
        // sketch sheets: one asset per layer, nested a level down
        const layers = props.get('layers');
        if (layers instanceof Y.Map) {
          layers.forEach((layer) => {
            if (!(layer instanceof Y.Map)) return;
            const assetId = layer.get('assetId');
            if (typeof assetId === 'string' && map[assetId]) layer.set('assetId', map[assetId]);
          });
        }
      });
    }, 'duplicate');
    doc.off('update', capture);
    if (!updates.length) return;
    await api.post(`/boards/${boardId}/updates`, {
      clientId: 'duplicate',
      updates: [bytesToBase64(updates.length === 1 ? updates[0]! : Y.mergeUpdates(updates))],
    });
  } finally {
    doc.destroy();
  }
}
